import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Bell, CircleDollarSign, Check, SlidersHorizontal, Play, X, ChevronLeft, BellRing, Banknote, ArrowLeftRight } from "lucide-react";
import { brl } from "../lib/api";
import { useNotifications, relativeLabel } from "../contexts/NotificationContext";
import { playPaymentSound, SOUND_LABELS } from "../lib/paymentSound";

const NOTIF_META = {
  "payment.received": { title: "Pagamento recebido", icon: CircleDollarSign, color: "emerald" },
  "withdrawal.requested": { title: "Saque solicitado", icon: Banknote, color: "amber" },
  "withdrawal.approved": { title: "Saque aprovado", icon: Banknote, color: "indigo" },
  "withdrawal.paid": { title: "Saque pago", icon: Banknote, color: "emerald" },
  "withdrawal.rejected": { title: "Saque rejeitado", icon: Banknote, color: "red" },
  "withdrawal.cancelled": { title: "Saque cancelado", icon: Banknote, color: "gray" },
  "transfer.request": { title: "Transferencia recebida", icon: ArrowLeftRight, color: "indigo" },
  "transfer.accepted": { title: "Transferencia aceita", icon: ArrowLeftRight, color: "emerald" },
  "transfer.rejected": { title: "Transferencia recusada", icon: ArrowLeftRight, color: "red" },
  "transfer.cancelled": { title: "Transferencia cancelada", icon: ArrowLeftRight, color: "gray" },
};
const COLOR_CLS = {
  emerald: ["bg-emerald-500/12 text-emerald-600", "text-emerald-700", "text-emerald-600"],
  amber: ["bg-amber-500/12 text-amber-600", "text-amber-700", "text-amber-600"],
  indigo: ["bg-indigo-500/12 text-indigo-600", "text-indigo-700", "text-indigo-600"],
  red: ["bg-red-500/12 text-red-600", "text-red-700", "text-red-600"],
  gray: ["bg-gray-500/12 text-gray-500", "text-gray-600", "text-gray-500"],
};

function useIsMobile() {
  const [m, setM] = useState(typeof window !== "undefined" ? window.matchMedia("(max-width:1023px)").matches : false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width:1023px)");
    const h = (e) => setM(e.matches);
    mq.addEventListener("change", h);
    return () => mq.removeEventListener("change", h);
  }, []);
  return m;
}

function useScrollLock(active) {
  useEffect(() => {
    if (!active) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [active]);
}

function Switch({ checked, onChange, testid }) {
  return (
    <button
      type="button" role="switch" aria-checked={checked}
      onClick={() => onChange(!checked)} data-testid={testid}
      className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${checked ? "bg-emerald-500" : "bg-[#D9CFD6]"}`}
    >
      <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${checked ? "left-[22px]" : "left-0.5"}`} />
    </button>
  );
}

function PrefRow({ label, desc, checked, onChange, testid }) {
  return (
    <div className="flex items-center justify-between gap-4 border-b border-border/60 px-4 py-3">
      <div className="min-w-0">
        <div className="text-[14px] font-medium text-brand-dark" style={{ overflowWrap: "anywhere" }}>{label}</div>
        {desc && <div className="text-[12px] text-[#8A7788]" style={{ overflowWrap: "anywhere" }}>{desc}</div>}
      </div>
      <Switch checked={checked} onChange={onChange} testid={testid} />
    </div>
  );
}

const HEADER = "sticky top-0 z-10 flex items-center justify-between border-b border-border bg-white px-4 py-3";

export default function NotificationBell({ idPrefix = "" }) {
  const ctx = useNotifications();
  const [view, setView] = useState(null); // null | 'list' | 'prefs'
  const isMobile = useIsMobile();
  const location = useLocation();
  const navigate = useNavigate();
  const btnRef = useRef(null);
  const panelRef = useRef(null);
  const open = view !== null;

  useScrollLock(open);

  const close = useCallback(() => {
    setView(null);
    if (btnRef.current) btnRef.current.focus();
  }, []);

  // Fecha ao trocar de rota
  const routeKey = location.pathname;
  useEffect(() => { setView(null); }, [routeKey]);

  // ESC fecha
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, close]);

  // Move foco para o painel ao abrir
  useEffect(() => { if (open && panelRef.current) panelRef.current.focus(); }, [open]);

  if (!ctx) return null;
  const { notifications, unread, prefs, pushState, markAllRead, savePrefs, enablePush } = ctx;

  const openList = () => { setView("list"); if (unread > 0) markAllRead(); };

  const onSelect = (n) => {
    close();
    if (n.url) navigate(n.url);
    else if (n.installment_id) navigate(`/cobrancas?installment=${n.installment_id}`);
  };

  const titleId = `${idPrefix}notif-title`;

  const ListContent = (
    <>
      <div className={HEADER}>
        <div id={titleId} className="text-[15px] font-semibold text-brand-dark">Notificacoes</div>
        <div className="flex items-center gap-1">
          <button onClick={markAllRead} title="Marcar como lidas" data-testid={`${idPrefix}notif-mark-read`} className="grid h-9 w-9 place-items-center rounded-lg text-[#6B5A69] hover:bg-muted"><Check size={18} /></button>
          <button onClick={() => setView("prefs")} title="Preferencias" data-testid={`${idPrefix}notif-prefs-btn`} className="grid h-9 w-9 place-items-center rounded-lg text-[#6B5A69] hover:bg-muted"><SlidersHorizontal size={18} /></button>
          <button onClick={close} aria-label="Fechar" data-testid={`${idPrefix}notif-close`} className="grid h-9 w-9 place-items-center rounded-lg text-[#6B5A69] hover:bg-muted"><X size={18} /></button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto overscroll-contain" data-testid={`${idPrefix}notif-list`}>
        {notifications.length === 0 ? (
          <div className="px-4 py-12 text-center text-sm text-[#9A8A98]" data-testid={`${idPrefix}notif-empty`}>Nenhuma notificacao ainda.</div>
        ) : (
          notifications.map((n) => {
            const meta = NOTIF_META[n.type] || NOTIF_META["payment.received"];
            const Icon = meta.icon;
            const [iconCls, titleCls, valueCls] = COLOR_CLS[meta.color] || COLOR_CLS.emerald;
            return (
            <button key={n.id} onClick={() => onSelect(n)} data-testid={`${idPrefix}notif-item-${n.id}`}
              className="flex w-full items-start gap-3 border-b border-border/60 px-4 py-3 text-left hover:bg-muted/60">
              <div className={`grid h-10 w-10 shrink-0 place-items-center rounded-full ${iconCls}`}><Icon size={19} /></div>
              <div className="min-w-0 flex-1">
                <div className={`text-[13px] font-semibold ${titleCls}`}>{n.title || meta.title}</div>
                {prefs.show_client && <div className="truncate text-[13px] text-brand-dark" style={{ overflowWrap: "anywhere" }}>{n.client_name}</div>}
                <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[11px] text-[#9A8A98]">
                  {prefs.show_value && n.amount != null && <span className={`font-medium ${valueCls}`}>{brl(n.amount)}</span>}
                  <span>·</span><span>{relativeLabel(n.created_at)}</span>
                </div>
              </div>
            </button>
            );
          })
        )}
      </div>
    </>
  );

  const PrefsContent = (
    <>
      <div className={HEADER}>
        <div className="flex items-center gap-1">
          <button onClick={() => setView("list")} aria-label="Voltar" data-testid={`${idPrefix}prefs-back`} className="grid h-9 w-9 place-items-center rounded-lg text-[#6B5A69] hover:bg-muted"><ChevronLeft size={20} /></button>
          <div id={titleId} className="text-[15px] font-semibold text-brand-dark">Preferencias</div>
        </div>
        <button onClick={close} aria-label="Fechar" data-testid={`${idPrefix}prefs-close`} className="grid h-9 w-9 place-items-center rounded-lg text-[#6B5A69] hover:bg-muted"><X size={18} /></button>
      </div>
      <div className="flex-1 overflow-y-auto overscroll-contain" data-testid={`${idPrefix}notif-prefs-panel`}>
        {pushState?.supported && pushState.permission !== "granted" && (
          <button onClick={enablePush} data-testid={`${idPrefix}enable-push-btn`}
            className="m-4 flex w-[calc(100%-2rem)] items-center gap-3 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-600 px-4 py-3 text-left text-white shadow-md">
            <BellRing size={20} />
            <div className="min-w-0">
              <div className="text-[13px] font-semibold">Ativar notificacoes push</div>
              <div className="text-[11px] opacity-90">Receba avisos mesmo com o app fechado</div>
            </div>
          </button>
        )}
        <div className="px-4 pt-3 text-[10px] uppercase tracking-widest text-[#9A8A98]">Notificacoes</div>
        <PrefRow label="Push Notifications" desc="Avisos nativos no dispositivo" checked={prefs.push_enabled} onChange={(v) => savePrefs({ push_enabled: v })} testid={`${idPrefix}pref-push`} />
        <PrefRow label="Toast" desc="Aviso dentro do app" checked={prefs.toast_enabled} onChange={(v) => savePrefs({ toast_enabled: v })} testid={`${idPrefix}pref-toast`} />
        <PrefRow label="Som" desc="Toca um som ao receber" checked={prefs.sound_enabled} onChange={(v) => savePrefs({ sound_enabled: v })} testid={`${idPrefix}pref-sound`} />
        <PrefRow label="Vibracao (PWA)" desc="Vibra no celular" checked={prefs.vibration_enabled} onChange={(v) => savePrefs({ vibration_enabled: v })} testid={`${idPrefix}pref-vibration`} />
        <PrefRow label="Tempo real" desc="Notificacao do navegador" checked={prefs.realtime_enabled} onChange={(v) => savePrefs({ realtime_enabled: v })} testid={`${idPrefix}pref-realtime`} />

        <div className="px-4 pt-4 text-[10px] uppercase tracking-widest text-[#9A8A98]">Tipos de evento</div>
        <PrefRow label="Pagamentos" checked={prefs.notif_payments} onChange={(v) => savePrefs({ notif_payments: v })} testid={`${idPrefix}pref-payments`} />
        <PrefRow label="Cobrancas" checked={prefs.notif_charges} onChange={(v) => savePrefs({ notif_charges: v })} testid={`${idPrefix}pref-charges`} />
        <PrefRow label="Clientes" checked={prefs.notif_clients} onChange={(v) => savePrefs({ notif_clients: v })} testid={`${idPrefix}pref-clients`} />
        <PrefRow label="Promocoes" checked={prefs.notif_promos} onChange={(v) => savePrefs({ notif_promos: v })} testid={`${idPrefix}pref-promos`} />
        <PrefRow label="Sistema" checked={prefs.notif_system} onChange={(v) => savePrefs({ notif_system: v })} testid={`${idPrefix}pref-system`} />

        <div className="px-4 pt-4 text-[10px] uppercase tracking-widest text-[#9A8A98]">Exibicao</div>
        <PrefRow label="Mostrar valor" checked={prefs.show_value} onChange={(v) => savePrefs({ show_value: v })} testid={`${idPrefix}pref-show-value`} />
        <PrefRow label="Mostrar cliente" checked={prefs.show_client} onChange={(v) => savePrefs({ show_client: v })} testid={`${idPrefix}pref-show-client`} />
        <PrefRow label="Resumo diario" desc="Total recebido no dia" checked={prefs.daily_summary} onChange={(v) => savePrefs({ daily_summary: v })} testid={`${idPrefix}pref-daily`} />

        {prefs.is_admin && (
          <div className="border-t border-border px-4 py-4">
            <div className="mb-2 text-[10px] uppercase tracking-widest text-[#9A8A98]">Som (definido pelo admin)</div>
            <div className="flex items-center gap-2">
              <select value={prefs.sound} onChange={(e) => savePrefs({ sound: e.target.value })} data-testid={`${idPrefix}pref-sound-select`}
                className="flex-1 rounded-lg border border-border bg-white px-3 py-2 text-[14px]">
                {(prefs.sound_options || Object.keys(SOUND_LABELS)).map((s) => <option key={s} value={s}>{SOUND_LABELS[s] || s}</option>)}
              </select>
              <button type="button" onClick={() => playPaymentSound(prefs.sound)} data-testid={`${idPrefix}pref-sound-test`} title="Testar som"
                className="grid h-10 w-10 place-items-center rounded-lg bg-emerald-500/12 text-emerald-600 hover:bg-emerald-500/20"><Play size={18} /></button>
            </div>
          </div>
        )}
        <div style={{ height: "env(safe-area-inset-bottom)" }} />
      </div>
    </>
  );

  const content = view === "prefs" ? PrefsContent : ListContent;

  const overlay = (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[9999]" data-testid={`${idPrefix}notif-overlay`}>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={close}
            className="absolute inset-0"
            style={{ background: "rgba(20,4,25,.35)", backdropFilter: "blur(2px)" }}
          />
          {isMobile ? (
            <motion.div
              ref={panelRef} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby={titleId}
              initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
              transition={{ type: "spring", stiffness: 360, damping: 34 }}
              data-testid={`${idPrefix}notif-panel`}
              className="absolute inset-x-0 bottom-0 flex max-h-[80dvh] flex-col overflow-hidden rounded-t-3xl bg-white shadow-2xl"
              style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
            >
              <div className="mx-auto mt-2 h-1.5 w-10 shrink-0 rounded-full bg-[#E2D8DF]" />
              {content}
            </motion.div>
          ) : (
            <motion.div
              ref={panelRef} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby={titleId}
              initial={{ opacity: 0, y: -8, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -8, scale: 0.98 }}
              transition={{ duration: 0.16 }}
              data-testid={`${idPrefix}notif-panel`}
              className="absolute flex flex-col overflow-hidden rounded-2xl border border-border bg-white shadow-pop"
              style={{ top: 72, right: 24, width: "min(420px, calc(100vw - 32px))", maxHeight: "calc(100vh - 96px)" }}
            >
              {content}
            </motion.div>
          )}
        </div>
      )}
    </AnimatePresence>
  );

  return (
    <>
      <button
        ref={btnRef} type="button" onClick={openList} aria-label="Notificacoes"
        aria-haspopup="dialog" aria-expanded={open}
        data-testid={`${idPrefix}notif-bell-btn`}
        className="relative grid h-11 w-11 shrink-0 place-items-center rounded-xl text-brand-dark transition-colors hover:bg-muted active:bg-muted"
      >
        <Bell size={22} strokeWidth={2} />
        {unread > 0 && (
          <span data-testid={`${idPrefix}notif-badge`}
            className="absolute right-1 top-1 grid min-h-[18px] min-w-[18px] place-items-center rounded-full bg-emerald-500 px-1 text-[10px] font-bold text-white">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>
      {typeof document !== "undefined" && createPortal(overlay, document.body)}
    </>
  );
}
